import type {
  DerivedThreadSelection,
  ThreadAsset,
  ThreadCluster,
  ThreadRow,
  ThreadSelectionState,
} from "./types";

function rowForSelection(rows: readonly ThreadRow[], selection: ThreadSelectionState): ThreadRow | null {
  const primary = selection.primary;
  if (primary.kind === "cell" || primary.kind === "row") {
    return rows.find((row) => row.runId === primary.rowId) ?? null;
  }
  return null;
}

export function deriveThreadSelection(
  rows: readonly ThreadRow[],
  assets: readonly ThreadAsset[],
  clusters: readonly ThreadCluster[],
  selection: ThreadSelectionState,
): DerivedThreadSelection {
  const selectedRow = rowForSelection(rows, selection);
  const primary = selection.primary;
  const selectedColId = primary.kind === "cell" ? primary.colId : null;
  const selectedRowId = selectedRow?.runId ?? null;
  const selectedFamily = selectedRow?.family ?? null;

  const litRowIds = new Set<string>();
  if (selectedRowId) litRowIds.add(selectedRowId);

  const litClusterIds = new Set<string>();
  const litAssetIds = new Set<string>();

  if (selectedRowId) {
    for (const asset of assets) {
      const touchesRow = asset.refs.rows?.includes(selectedRowId) ?? false;
      const touchesCell = selectedColId
        ? asset.refs.cells?.some((cell) => cell.runId === selectedRowId && cell.col === selectedColId) ?? false
        : false;
      if (touchesRow || touchesCell) {
        litAssetIds.add(asset.id);
        for (const upstream of asset.refs.upstream ?? []) litClusterIds.add(upstream);
      }
    }
  }

  if (primary.kind === "asset") {
    const asset = assets.find((item) => item.id === primary.assetId);
    for (const rowId of asset?.refs.rows ?? []) litRowIds.add(rowId);
    for (const upstream of asset?.refs.upstream ?? []) litClusterIds.add(upstream);
    if (asset) litAssetIds.add(asset.id);
  }

  if (primary.kind === "cluster") litClusterIds.add(primary.clusterId);
  if (litClusterIds.size === 0 && selectedRow) {
    for (const cluster of clusters) {
      if (cluster.type === "run" || cluster.type === "table") litClusterIds.add(cluster.id);
    }
  }

  return {
    selectedRowId,
    selectedColId,
    selectedFamily,
    litRowIds,
    litClusterIds,
    litAssetIds,
  };
}

export const emptyThreadSelection: ThreadSelectionState = {
  primary: { kind: "none" },
};
